import { HOW_TO_USE_SEARCH_PAYLOAD_VIDEO_URL } from '@aibees/elephant/constants';
import {
  CustomDialog,
  Icon,
  JsonEditor,
  VideoModal,
} from '@aibees/shared/components';
import {
  useBoolean,
  useCopyToClipboard,
  useNotify,
} from '@aibees/shared/hooks';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { useSearchPayload } from './use-search-payload';

type Props = {
  state: ReturnType<typeof useBoolean>;
};

/** Footer-left button that plays the feature tutorial video (same pattern as the
 *  Bulk Include Cities dialog's help-video button). */
const HowItWorksButton = memo(() => {
  const videoState = useBoolean();
  return (
    <>
      <Button
        variant="outlined"
        color="default"
        size="small"
        startIcon={<Icon name="play-circle-outline" />}
        onClick={videoState.onTrue}
      >
        How it works
      </Button>
      <VideoModal
        state={videoState}
        videoUrl={HOW_TO_USE_SEARCH_PAYLOAD_VIDEO_URL}
        title="How it works"
      />
    </>
  );
});
HowItWorksButton.displayName = 'HowItWorksButton';

/**
 * Shows the current search as readable JSON with Copy and Apply actions. Apply
 * validates structurally and, on success, replaces the scope's params so the
 * filters/results update; errors are shown inline and block the apply.
 */
export const SearchPayloadDialog = memo(({ state }: Props) => {
  const { json, apply, matchesCurrent } = useSearchPayload();
  const { copy } = useCopyToClipboard();
  const notify = useNotify();

  const [text, setText] = useState(json);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChange = (value: string) => {
    setText(value);
    if (errors.length) setErrors([]);
  };

  // Pull the line number out of a JSON parse error ("... line 52 column 5") to
  // highlight it in the editor gutter.
  const errorLine = useMemo(() => {
    const match = errors[0]?.match(/line (\d+)/i);
    return match ? Number(match[1]) : undefined;
  }, [errors]);

  const handleCopy = async () => {
    const ok = await copy(text);
    if (ok) notify.success('Copied to clipboard!');
    else notify.error('Could not copy to clipboard.');
  };

  const handleApply = ({ toggle }: { toggle: () => void }) => {
    const result = apply(text);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors([]);
    notify.success('Search payload applied.');
    toggle();
  };

  return (
    <CustomDialog
      state={state}
      title="Search Payload"
      icon="code-outline"
      iconSx={{ color: 'primary.main' }}
      maxWidth="sm"
      actionsSx={{ borderColor: 'grey.300' }}
      actionsPrepend={
        <Box sx={{ flex: 1, display: 'flex' }}>
          <HowItWorksButton />
        </Box>
      }
      actions={[
        {
          children: 'Copy Payload',
          variant: 'outlined',
          color: 'default',
          size: 'small',
          onClick: handleCopy,
        },
        {
          children: 'Apply Payload',
          variant: 'contained',
          color: 'primary',
          size: 'small',
          disabled: !text.trim() || matchesCurrent(text),
          onClick: handleApply,
        },
      ]}
    >
      <Stack spacing={1.5}>
        <Alert severity="info">
          Copy this JSON and give it to an AI agent to tweak the values to your
          needs. Paste the updated payload back here and click Apply to run the
          new search. Change only the values — keep the field names and
          structure.
        </Alert>

        <JsonEditor
          value={text}
          onChange={handleChange}
          error={errors.length > 0}
          errorLine={errorLine}
        />

        {errors.length > 0 && (
          <Box>
            {errors.map((err, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ display: 'block', color: 'error.main', fontSize: 12 }}
              >
                • {err}
              </Typography>
            ))}
          </Box>
        )}
      </Stack>
    </CustomDialog>
  );
});

SearchPayloadDialog.displayName = 'SearchPayloadDialog';
